import React, {useMemo} from 'react';
import {Box, Grid} from '@mui/material';
import {mainPlanTypeType, recommendFieldsType} from '../recommendFields';
import {contributeAmountField as recommendAmountField} from '../../RecommendsContribute/contributeFields';
import {PotDetails} from '../../../../schema-types/PotDetails';
import useComboFrequency from '../../../../hooks/useComboFrequency';
import AutoField from '../../../../components/AutoForm/AutoFieldNew';
import {useRecommendContext} from '../CreateRecommendHelpers';
import {ComboFrequencyEnum} from '../../../../schema-types/globalTypes';

import {PlanFeesField} from '../PlanFees';
import {shouldShowEmployerContribution} from '../../RecommendsContribute/ContributeHelpers';
import {useAutoForm} from '../../../../components/AutoForm/useAutoForm';
import {useIsMinor} from '../../hooks/createRecommendHooks';

export const ContributeSubplanDestinationAmounts = ({
  potInner,
  formikKeyPrefix,
  disabled,
  mainPlanType,
  comboFrequency,
}: {
  potInner: PotDetails;
  formikKeyPrefix: string;
  disabled: boolean;
  mainPlanType: mainPlanTypeType;
  comboFrequency: ComboFrequencyEnum | undefined;
}) => {
  const {recommendDetails} = useRecommendContext();
  const {values} = useAutoForm<recommendFieldsType>();
  const isMinor = useIsMinor();
  const potWrapper = useMemo(() => {
    return recommendDetails?.pots?.find((curr) => curr.pot.id === potInner.id);
  }, [recommendDetails, potInner]);

  const {hasSingle, hasRecurring} = useComboFrequency(comboFrequency);
  const showEmployerContribution = shouldShowEmployerContribution({
    isMinor,
    mainPlanType,
  });
  const showSingleEmployers = showEmployerContribution && hasSingle;
  const showRecurringPersonal = hasRecurring;
  const showRecurringEmployers = showEmployerContribution && hasRecurring;

  return (
    <Box mt={1}>
      <Grid container alignContent="flex-start" direction="row" spacing={3}>
        {hasSingle ? (
          <Grid item xs={6}>
            <AutoField
              field={{
                ...recommendAmountField,
                allowZero: true,
                canBeEmpty: true,
                disabled,
                id: `${formikKeyPrefix}.singleClientAmount`,
                label: `Single Amount${mainPlanType === 'pension' ? ' (Gross)' : ''}`,
              }}
            />
          </Grid>
        ) : null}

        {showRecurringPersonal ? (
          <Grid item xs={6}>
            <AutoField
              field={{
                ...recommendAmountField,
                disabled,
                allowZero: true,
                canBeEmpty: true,
                id: `${formikKeyPrefix}.recurringClientAmount`,
                label: `Recurring Amount${mainPlanType === 'pension' ? ' (Gross)' : ''}`,
              }}
            />
          </Grid>
        ) : null}
        {/* For ALL pension contributions, add employer contributions */}
        {showSingleEmployers && (
          <Grid item xs={6}>
            <AutoField
              field={{
                ...recommendAmountField,
                disabled,
                allowZero: true,
                canBeEmpty: true,
                id: `${formikKeyPrefix}.singleEmployerAmount`,
                label: 'Employer Single Amount (Gross)',
              }}
            />
          </Grid>
        )}
        {/* For ALL contributions, allow recurring */}
        {/* For recurring pension contributions, ask for employers recurring amount */}
        {showRecurringEmployers ? (
          <Grid item xs={6}>
            <AutoField
              field={{
                ...recommendAmountField,
                disabled,
                allowZero: true,
                canBeEmpty: true,
                id: `${formikKeyPrefix}.recurringEmployerAmount`,
                label: 'Employer Recurring Amount (Gross)',
              }}
            />
          </Grid>
        ) : null}

        <Grid item xs={12}>
          <PlanFeesField
            pot={potInner}
            inModify={false}
            hasSubplan={false}
            requiresMandate={false}
            productId={potWrapper?.pot.productName?.id}
            providerId={potWrapper?.pot.productProvider?.id}
            formikKeyPrefixWithoutFee={formikKeyPrefix}
            disabled
            helperText="Cannot edit existing ongoing fee using this requirement"
            isSaltus={!!potWrapper?.pot.productProvider?.isSaltus}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
